import { useState, useEffect, useRef } from 'react'
import { formatCalendarDay } from '../utils/dateUtils'
import { currentEncounterNumber } from '../utils/caseEncounters'
import { formatMoneyCents, formatMoneyDollars } from '../utils/money'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, ChevronRight, Edit3, Calendar, Clock, MapPin, Shield, X, Wallet, Lock } from 'lucide-react'
import { useCaseStore, useProfileStore, useUserStore } from '../stores'
import { casesAPI } from '../services/api'
import { useI18n } from '../i18n'
import { useAgent } from './AgentContext'
import QRCodeDisplay from '../utils/qrcode'
import { formatAddress } from '../utils/formatAddress'
import EligibilityBadge, { roleFromSpecialty, fmtRelative } from './EligibilityBadge'
import NetworkDisclaimer from './NetworkDisclaimer' // s105 (TestFlight b71)
import { eligibilityAnswer, eligibilityKey, storedResultStillFits, limitNoticeOf, limitNoticeKey, limitNoticeParams } from '../utils/eligibilityLimit'
import WalletPassModal from './WalletPassModal'
import JoinVideoVisit from './JoinVideoVisit'
import VideoSignOnFlow from './VideoSignOnFlow' // s114 video sign-on (canvas V2/V3)
import { storedSignOn, stateName } from '../utils/videoSignOn'
import { videoJoinTarget, consentAppointmentId, joinIsOpen } from '../utils/videoVisit'
import { APPLE_WALLET_ENABLED, GOOGLE_WALLET_ENABLED, addAppleWalletPass } from '../utils/walletPass'
import { deriveAuthRequired } from '../utils/authRequired'
import { roleCopayCents } from '../utils/copayFigures'
import { hasCarrierAndMemberId } from '../utils/discoverySearch' // PROD-1132 Q2
import { PatientUploadSection } from '../pages/case/AuthorizationsScreen'
import StoredDocViewer from './StoredDocViewer'
import { clinicianName } from '../utils/clinicianName.js'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

// ═══════════════════════════════════════════════════════
// CheckInModal v181.8.3
// Pre-visit check-in: Visit Details → ID → Insurance/Copay → (HMO) Authorization → QR
// s101 Phase 3 (Dr. Rami 2026-10-12): self-reported vitals are removed from every check-in;
// vitals are taken in the office (MA vitals in the panel).
// Lives on DiagnoseScreen when appointment is confirmed
// v181.8.3: Suppress FAB while modal is open, copay formatting
// ═══════════════════════════════════════════════════════

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LIGHT_BLUE = '#A7D9FC'
const ICE_BLUE = '#E9F8FF'
const GREEN = '#66D170'

// ★★★ v242.88.0 — `WALLET_ENABLED = false` LIVED HERE AND IT WAS HALF WRONG
//   FOR LONG ENOUGH THAT IT HID A WORKING FEATURE.
//   The comment said "the endpoints return 501 until then". That was true when
//   it was written and stopped being true at API v243.8.0, when Apple pass
//   signing went live — and nothing re-reads a comment. Measured 2026-08-20:
//   Apple answers 404 on a bogus case id (past the cert check, so CONFIGURED),
//   Google answers 501 (NOT configured).
//   ★★ ONE BOOLEAN WAS GATING TWO FEATURES WITH TWO DIFFERENT SERVER
//      READINESSES, so it could not be right about both. That is the real
//      defect shape, not "someone forgot to flip a flag".
//   Both flags, the probe that decides them, and the add-to-wallet call now
//   live in src/utils/walletPass.js — one place, because this file and
//   WalletPassModal.jsx each had their own copy and fixing one left the other.

function StepDots({ current, total }) {
  return (
    <div className="flex items-center justify-center gap-1.5 py-2.5">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className="h-[6px] rounded-full transition-all duration-300"
          style={{ width: i === current ? 20 : 7, backgroundColor: i === current ? BLUE : i < current ? GREEN : '#E2E8F0' }} />
      ))}
    </div>
  )
}

function FieldRow({ label, value, last }) {
  return (
    <div className={`py-2.5 ${!last ? 'border-b border-[#F1F5F9]' : ''}`}>
      <p className="text-[11px] text-[#94A3B8] font-medium mb-0.5">{label}</p>
      <p className="text-[14px] text-[#15354F] font-medium">{value || '—'}</p>
    </div>
  )
}

function ConfirmCheckbox({ checked, onChange, label }) {
  return (
    <button onClick={onChange} className={`flex items-center gap-3 w-full px-4 py-3.5 rounded-[14px] border-[1.5px] text-left transition-all ${checked ? 'border-[#66D170] bg-[#F0FDF4]' : 'border-[#E2E8F0] bg-white'}`}>
      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${checked ? 'bg-[#66D170] border-[#66D170]' : 'border-[#CBD5E1] bg-white'}`}>
        {checked && <Check size={14} className="text-white" strokeWidth={3} />}
      </div>
      <span className="text-[14px] font-semibold text-[#15354F]">{label}</span>
    </button>
  )
}

export default function CheckInModal({ isOpen, onClose, doctor, appointment, joinIntent = false }) {
  const navigate = useNavigate()
  const { currentCase, updateCase } = useCaseStore()
  const profile = useProfileStore((s) => s.profile)
  const accountPhone = useUserStore((s) => s.user?.phone) || ''
  const { t, hasText, language } = useI18n()
  const { suppressFab } = useAgent()

  const [step, setStep] = useState(0)
  // s114: a VIDEO visit's sign-on (identity, location, consent). s126c (Dr. Rami 2026-11-01): asked when the
  //   patient taps Join, never as a step of the check-in. joinIntent = this modal was opened FROM a Join tap
  //   (Visits / Diagnose / Deliver): it joins by itself once checked in (sign-on first when not yet recorded).
  const [signOnOpen, setSignOnOpen] = useState(false)
  const [signOn, setSignOn] = useState(null)
  const pendingJoinRef = useRef(null)
  const [idConfirmed, setIdConfirmed] = useState(false)
  const [copayAcknowledged, setCopayAcknowledged] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [checkInToken, setCheckInToken] = useState(null)
  const [showWalletPass, setShowWalletPass] = useState(false)
  // v242.88.0 (WALLET-STRAND-1): adding a pass is now a fetch + share sheet
  // rather than a navigation, so it has a busy state and can fail visibly.
  const [walletBusy, setWalletBusy] = useState(false)
  const [walletError, setWalletError] = useState('')
  const handleAddToWallet = async () => {
    if (walletBusy) return
    setWalletBusy(true)
    setWalletError('')
    try {
      await addAppleWalletPass(currentCase?.id)
    } catch (err) {
      console.error('[Wallet] add failed:', err)
      setWalletError(err?.message || 'Could not add the pass.')
    } finally {
      setWalletBusy(false)
    }
  }
  const [error, setError] = useState('')

  // v222.2.0 Stedi (Seam 3): re-verify eligibility on step 2 if last check >12h stale
  const CHECKIN_RECHECK_WINDOW_MS = 12 * 60 * 60 * 1000  // 12 hours
  const [eligibilityChecking, setEligibilityChecking] = useState(false)
  const [eligibility, setEligibility] = useState(null)
  // ★ PROD-689: the API's recheck_limit / recheck_unavailable answer (limitNoticeOf), or null.
  const [limitNotice, setLimitNotice] = useState(null)
  // ★ PROD-901: true only when THIS modal's own step-2 check answered with a result and no limit
  //   notice. A stored result (shown at once, or kept inside CHECKIN_RECHECK_WINDOW_MS without a
  //   new check) is not "Confirmed ... just now". Held by check-checkin-limit-notice CN-3 / CN-4
  //   and check-eligibility-trigger ET-7.
  const [eligibilityFresh, setEligibilityFresh] = useState(false)
  // ★ PROD-919: the step-2 check, at most one per opening. Each POST .../eligibility-check is a paid
  //   payer call that a client-side cancel does not stop. eligibilityRunRef holds the last check
  //   ({ key, openNo, settled, promise }) so a return to step 2 (in this opening, or a re-open while
  //   it is still out) attaches to it instead of starting another; openNoRef counts the openings.
  //   Gate: check-checkin-eligibility-once.
  const eligibilityRunRef = useRef(null)
  const openNoRef = useRef(0)
  const updateProfileLocal = useProfileStore((s) => s.updateProfile)

  // v235.2.0 — authorization gate. authRequired is known at mount from the
  // profile's last_eligibility (the step-2 re-verify only refreshes copay).
  const [authConfirmed, setAuthConfirmed] = useState(false)
  const authRequired = deriveAuthRequired(profile?.insurance, eligibility || profile?.insurance?.last_eligibility)
  const caseAuth = currentCase?.authorization || null
  // ★★ s101 Phase 3 (x_18, TestFlight b65): an authorization the MD uploaded in the panel lives on
  //   case.adminDocuments.authorization (uploadedBy 'md'), not in case.authorization, so check-in
  //   asked the patient for a document their doctor already had. Either source is "on file"; the
  //   newest MD upload is the one shown.
  const mdAuthDoc = (() => {
    const docs = currentCase?.adminDocuments || currentCase?.documents || {}
    const list = Array.isArray(docs.authorization) ? docs.authorization : []
    const md = list.filter((d) => d && d.uploadedBy === 'md')
    return md.length ? md[md.length - 1] : null
  })()
  const authOnFile = !!((caseAuth && (caseAuth.authorizationNumber || caseAuth.status === 'confirmed')) || mdAuthDoc)
  const [viewingAuthDoc, setViewingAuthDoc] = useState(false)
  // Named step indices so the conditional auth step doesn't require renumbering.
  // details/identity/insurance never move; auth slots at 3 (HMO only), then success.
  const IDX = {
    details: 0, identity: 1, insurance: 2,
    authorization: authRequired ? 3 : -1,
    success: authRequired ? 4 : 3,
  }
  const TOTAL_DOTS = authRequired ? 4 : 3
  // ★ s101 Phase 3: an authorization on file is CONFIRMED by the patient in one tap; it no longer
  //   clears the step by itself. A patient upload confirms it (and check-in completes at once).
  const authCleared = authConfirmed

  // ★ s101 Phase 3: the front desk's arrival scan (stage ready → arrived) reaches the patient. While
  //   the success screen is open and the code is not yet scanned, re-read the case every 20 s; the
  //   check_in it carries says 'arrived' (or 'verified', the doctor's scan) with arrivedAt.
  const stageNow = currentCase?.checkIn?.stage
  const present = stageNow === 'arrived' || stageNow === 'verified'
  const pollRef = useRef(null)
  useEffect(() => {
    if (!isOpen || step !== IDX.success || present || !currentCase?.id) return undefined
    let stopped = false
    const tick = async () => {
      if (stopped || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return
      try {
        const r = await casesAPI.get(currentCase.id)
        const ci = (r && (r.case || r) || {}).checkIn
        if (!stopped && ci && (ci.stage === 'arrived' || ci.stage === 'verified')) updateCase({ checkIn: ci })
      } catch (e) { /* the next tick tries again */ }
    }
    pollRef.current = setInterval(tick, 20000)
    return () => { stopped = true; clearInterval(pollRef.current) }
  }, [isOpen, step, present, currentCase?.id])

  // v181.8.3: Suppress FAB while modal is open
  useEffect(() => {
    suppressFab(isOpen)
    return () => suppressFab(false)
  }, [isOpen, suppressFab])

  // Reset when opening — but if already checked in, show QR code (step 4)
  useEffect(() => {
    if (isOpen) {
      openNoRef.current += 1
      const existingToken = currentCase?.checkIn?.token
      if (currentCase?.status === 'checked_in' && existingToken) {
        // Already checked in — show QR code directly
        setStep(IDX.success)
        setCheckInToken(existingToken)
        setError('')
        // s126c: the stored sign-on of THIS visit, if any; a missing one is asked at Join, not here
        setSignOn(storedSignOn(currentCase?.checkIn, signOnApptId))
      } else {
        setStep(0); setIdConfirmed(false); setCopayAcknowledged(false); setAuthConfirmed(false)
        setSubmitting(false); setCheckInToken(null); setError(''); setLimitNotice(null); setEligibilityFresh(false)
      }
    } else {
      // ★ PROD-919: leave step 2 on close. The modal stays mounted, so a re-open first renders the
      //   step it was closed on: the step-2 effect fired a check there that the reset to step 0
      //   cancelled in the same commit (a paid call nobody saw).
      setStep(0)
    }
  }, [isOpen])

  // v222.2.0 Stedi (Seam 3): Re-verify eligibility when patient lands on step 2 (Insurance & billing)
  // Fires if last_eligibility is stale (>12h) or for a different doctor than this appointment.
  // Self-pay patients skip silently.
  useEffect(() => {
    if (!isOpen || step !== 2) return
    if (!currentCase?.id || String(currentCase.id).startsWith('local-')) return
    if (!doctor?.id) return

    const ins = profile?.insurance || {}
    if (ins.selfPay || ins.coverageType === 'self-pay' || ins.coverageType === 'hsa') return
    if (!ins.memberId) return

    // Surface existing eligibility from profile store immediately
    const existing = ins.last_eligibility
    if (existing) setEligibility(existing)

    // ★ PROD-919: the check of THIS opening (out or answered), or one still out from an earlier
    //   opening, for the same case, doctor and insurance is attached to, never started again.
    //   ★ review r_919 S2-1: the insurance is eligibilityKey (carrier, member ID and, when the
    //   patient is not the policyholder, the subscriber), the owner of "checked for"; a carrier or
    //   policyholder fixed under "Update my insurance" with the same member ID is a new check. The
    //   member ID alone stands in only when there is no carrier name (eligibilityKey null).
    //   ★ review r2_919 N3: `|` plus the member ID or nothing, so a reset store (no member ID) gives
    //   `|`, which no run key holds (a run needs a member ID).
    //   ★ review r2_919 N2: that stand-in is narrower than what the server checks with (payer id,
    //   insurer, policyholder), so a run under it is attached to only in the opening that started
    //   it; a later opening starts a new check, as 9f6d250a did.
    const insKeyOf = (i) => eligibilityKey(i) || `|${i?.memberId || ''}`
    const insKey = insKeyOf(ins)
    const carrierKeyed = !!eligibilityKey(ins)
    const runKey = `${currentCase.id}|${doctor.id}|${insKey}`
    // ★ review r2_919 N1: the signed-in account the check is asked for (useUserStore user.id).
    const userIdNow = () => { const id = useUserStore.getState().user?.id; return id == null || id === '' ? null : String(id) }
    let run = eligibilityRunRef.current
    if (!run || run.key !== runKey || (run.openNo !== openNoRef.current && (run.settled || !carrierKeyed))) {
      // PROD-975: keep the stored result only when it provably belongs to the insurance on file
      //   (storedResultStillFits: checkedKey, else the member-ID proof), not just by age and doctor.
      //   Without that proof (a result with no checkedKey and no subscriberConfirmed, a member ID,
      //   carrier or policyholder edited after it) one check is asked, the safe side.
      const shouldRefire = !storedResultStillFits(existing, ins, doctor, CHECKIN_RECHECK_WINDOW_MS)
      if (!shouldRefire) {
        // PROD-975 (r2_919 N3): no new check starts, so a check still out for ANOTHER key (the old
        //   doctor's, after a doctor change on step 2) is orphaned: its late answer fails the
        //   identity guard below and writes nothing over the result kept here.
        if (run && run.key !== runKey) eligibilityRunRef.current = null
        return
      }

      const started = { key: runKey, openNo: openNoRef.current, settled: false, promise: null, userId: userIdNow() }
      // ★ PROD-936 PAT-2: skipCache false. The server reuses the stored result of this coverage and this
      //   doctor for 30 days (reused: true, no payer call) and says so; the card then shows its age
      //   (eligibility.reusedNotice) and never "Verified ... just now" (limitNoticeOf gives a notice).
      started.promise = casesAPI.checkEligibility(currentCase.id, { skipCache: false })
        .then((resp) => {
          // ★ PROD-689 client half (P4-4): through the one reader. A limit answer (recheck_limit /
          //   recheck_unavailable, dormant on the live API) carries the STORED result or null:
          //   the stored one is shown as it is, null leaves the card as it was. Check-in is never
          //   blocked by it. ★ PROD-689 (train 2026-10-12): the card now says so in one approved
          //   line (eligibility.limitCheckedToday / limitUnavailable) and stops calling the copay
          //   "Verified ... just now". Held by check-eligibility-trigger ET-7 and check-browser-render.
          const answer = eligibilityAnswer(resp)
          // ★ PROD-919: kept even when the modal was closed meanwhile (the call is paid either
          //   way), unless a newer check for another case, doctor or insurance replaced this one.
          //   ★ review r_919 S2-2: and only while the profile store still holds the insurance it
          //   was asked for. A member ID, carrier or policyholder edited meanwhile (Profile's own
          //   check may have stored a newer result) and a sign-out (reset store, no insurance) give
          //   another key, so the late answer is not written.
          //   ★ review r2_919 N1: another account can hold the SAME key (a sibling on the same plan
          //   and policyholder), so the write also needs the signed-in user id the check started
          //   under, unchanged and present; a sign-out or another account's sign-in fails that.
          const currentIns = useProfileStore.getState().profile?.insurance || {}
          if (answer.eligibility && eligibilityRunRef.current === started && started.userId && userIdNow() === started.userId && insKeyOf(currentIns) === insKey) {
            // Persist to local profile store so all surfaces (Profile → Insurance, Share, etc.) see the fresh check
            if (updateProfileLocal) {
              // PROD-975: a real answer of THIS run is tagged with the key it was asked for (what the
              //   server will stamp), so a re-open inside the window keeps it. A limit answer carries
              //   the server's STORED result, checked for whatever it last asked: never tagged.
              const own = answer.limited ? answer.eligibility : { ...answer.eligibility, checkedKey: insKey }
              updateProfileLocal({ insurance: { ...currentIns, last_eligibility: own } })
            }
          }
          return answer
        })
        .finally(() => { started.settled = true })
      eligibilityRunRef.current = started
      run = started
    }

    let cancelled = false
    setLimitNotice(null)
    setEligibilityFresh(false)
    setEligibilityChecking(true)
    run.promise
      .then((answer) => {
        if (cancelled) return
        const notice = limitNoticeOf(answer)
        setLimitNotice(notice)
        // ★ PROD-901: only this answer, fresh from the payer and not limited, may read "Verified".
        setEligibilityFresh(!notice && !!answer.eligibility)
        if (answer.eligibility) setEligibility(answer.eligibility)
      })
      .catch((e) => { if (!cancelled) console.warn('[CHECKIN] eligibility recheck failed:', e?.message || e) })
      .finally(() => { if (!cancelled) setEligibilityChecking(false) })

    // ★ review r_train3 S3: a check cancelled here (step change, close, doctor change) answers into
    //   nothing, so its finally never clears the running flag; when the next arrival on step 2
    //   fires no check (an in-window result arrived meanwhile), the card would say "Verifying
    //   coverage" for the rest of the mount. A re-fire sets it again. Gate: check-eligibility-trigger ET-7s.
    return () => { cancelled = true; setEligibilityChecking(false) }
  }, [isOpen, step, currentCase?.id, doctor?.id])

  if (!isOpen) return null

  // Extract patient data from profile
  const identification = profile?.identification || {}
  const insurance = profile?.insurance || {}
  const patientName = `${identification.firstName || ''} ${identification.lastName || ''}`.trim() || 'Patient'
  // v225.0.x: values for the Wallet-style Visit Pass opened from the check-in confirm screen.
  const passDateLabel = (() => {
    const d = appointment?.date || appointment?.requestedDate || appointment?.confirmedDate
    if (!d) return ''
    try { const [y, mo, dy] = d.split('-').map(Number); return new Date(y, mo - 1, dy).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) } catch { return d }
  })()
  const passTimeLabel = (() => {
    const tt = appointment?.time || appointment?.requestedTime || appointment?.confirmedTime
    if (!tt) return ''
    const [h, m] = tt.split(':').map(Number); const ampm = h >= 12 ? 'PM' : 'AM'; const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`
  })()
  const passProviderName = doctor ? clinicianName(doctor) : ''
  // v242.79.0 (TF-36) — "Check in pass for virtual visit needs to be modified
  // to reflect that - should have the zoom link." The pass printed the
  // practice's street address for every visit, so a patient attending by video
  // was handed directions to a building.
  const isVirtualVisit = appointment?.type === 'video' || appointment?.type === 'telehealth' || appointment?.type === 'virtual'
  // MEET PA2: the caller hands the visit's join target (videoJoinTarget of the appointment row) as
  //   `join`; an older caller's bare link fields resolve through the same owner (meeting_url first).
  //   The pass PRINTS the link (TF-36); the only tap that opens it is the JoinVideoVisit below.
  const visitJoin = appointment?.join || videoJoinTarget(null, appointment)
  const visitJoinLink = visitJoin?.meeting_url || ''
  // s114: the appointment row the video sign-on is recorded on (a uuid; synthesized rows have none)
  const signOnApptId = consentAppointmentId(appointment) || consentAppointmentId(appointment?.join)
  const passAddressLine1 = isVirtualVisit
    ? (visitJoinLink || 'Virtual visit - link to follow')
    : (doctor?.practice_address ? `${formatAddress(doctor.practice_address)}${doctor.practice_city ? `, ${doctor.practice_city}` : ''}` : '')
  const dob = identification.dateOfBirth || identification.dob || ''
  // PROD-1167: the account number first (users.phone), the profile copy only as a fallback.
  const phone = accountPhone || identification.phone || ''
  // v242.79.0 (TF-34) — "User full address needs to appear in addition to city
  // state zip." It was never going to. ProfileIdentification writes
  // `streetAddress`, `apt` and `zipCode` (its line 26); this read `address` and
  // `zip`. Only `city` and `state` matched by name, which is exactly what the
  // screenshot showed: "Los Angeles, CA". Nothing was missing from the profile
  // — two of the four fields were being asked for by the wrong name, and
  // `.filter(Boolean)` then quietly dropped the undefineds so it looked
  // deliberate. Old keys kept as fallbacks for any row written before this.
  const street = [identification.streetAddress || identification.address, identification.apt]
    .filter(Boolean).join(' ')
  const address = [street, identification.city, identification.state,
                   identification.zipCode || identification.zip]
    .filter(Boolean).join(', ')

  // Determine copay from insurance
  const doctorSpecialty = ((doctor?.specialties && doctor.specialties[0]) || '') || ''
  const isPCP = /primary|family|internal|general/i.test(doctorSpecialty)
  const rawCopay = isPCP
    ? (insurance.copayOfficeVisit || insurance.copayPCP || insurance.copay?.officeVisit || null)
    : (insurance.copaySpecialist || insurance.copay?.specialist || insurance.copayOfficeVisit || insurance.copayPCP || insurance.copay?.officeVisit || null)
  // v230.2.0: normalize static profile copay via shared formatter ($55 / $55.50).
  const staticCopayAmount = formatMoneyDollars(rawCopay)
  // v222.2.0: Prefer verified copay from the latest eligibility check; fall back to static profile copay.
  // PROD-976: the role's own figure only (utils/copayFigures.js).
  const verifiedCopayCents = roleCopayCents(eligibility, isPCP ? 'pcp' : 'specialist')
  const verifiedCopayAmount = formatMoneyCents(verifiedCopayCents)
  const copayAmount = verifiedCopayAmount || staticCopayAmount
  // ★ PROD-689 (Alex 2026-10-11 21:4x: "Estimated copay"): a limit answer's result is the STORED one,
  //   not a fresh confirmation, so the card never says "Verified" / "just now" while a notice is set.
  // ★ PROD-901: nor for a STORED result (not re-fired inside the 12-hour window, or still on screen
  //   while the new check runs): it shows as "Estimated copay" — the same existing strings.
  const copayIsVerified = !!verifiedCopayAmount && eligibility?.status === 'active' && !limitNotice && eligibilityFresh
  const copayLabel = isPCP ? t('checkIn.copayCard.officeVisit') : t('checkIn.copayCard.specialist')

  // v242.79.0 (TF-35) — "if self pay, the price of the visit needs to populate."
  // Self-pay skips the eligibility check (line ~146 returns early, correctly —
  // there is no coverage to verify), and every number on this screen came from
  // insurance, so a self-pay patient reached the confirm step having been shown
  // no price at all.
  //
  // The rate exists and the API already flattens it onto the doctor:
  // accept_self_pay, self_pay_in_office_rate, self_pay_virtual_rate.
  // ★ PER MODALITY, so it must be picked by THIS appointment's type. Showing an
  // in-office rate for a virtual visit is not a cosmetic error — it is quoting
  // the patient the wrong price.
  const isSelfPay = !!(insurance.selfPay || insurance.coverageType === 'self-pay' || insurance.coverageType === 'hsa')
  const selfPayRate = isSelfPay
    ? (isVirtualVisit ? doctor?.self_pay_virtual_rate : doctor?.self_pay_in_office_rate)
    : null
  const selfPayAmount = formatMoneyDollars(selfPayRate)

  // v237.18.0: Unify the coverage verifier with the copay card. Result-class flags
  // (mirror EligibilityBadge.deriveResultClass) drive a single coverage+copay block:
  //   • checking            → in-card spinner ("Verifying coverage…")
  //   • active / verified   → ice copay card with VERIFIED badge + "Confirmed … just now"
  //   • resolved non-active → detailed EligibilityBadge (keeps inactive/not-found/error
  //                           copy + CTAs) followed by the static estimated copay card
  const eligClass = (eligibility?.resultClass || eligibility?.status || '').toLowerCase()
  const eligibilityActive = eligClass === 'active' || eligClass === 'verified'
  const eligibilityResolvedNonActive = !!eligibility && !eligibilityChecking && !eligibilityActive
  // Show the in-card verifying state only while we don't yet have a verified copay to display.
  const eligibilityVerifying = eligibilityChecking && !copayIsVerified && !eligibilityResolvedNonActive
  const payerName = insurance.provider || insurance.insurerName || 'your insurance'

  // Format DOB for display
  const formatDOB = (d) => {
    if (!d) return '—'
    return formatCalendarDay(d, { year: 'numeric', month: 'long', day: 'numeric' }, 'en-US') || d
  }

  // `authNow` — a patient upload confirms the authorization and completes check-in in the same
  //   tick, before setAuthConfirmed(true) has re-rendered.
  const handleSubmit = async (authNow = false) => {
    if (!currentCase?.id || submitting) return
    setSubmitting(true)
    setError('')
    try {
      const response = await casesAPI.checkIn(currentCase.id, {
        // v222.10.1: pass the cassette key so a follow-up check-in resolves the
        // correct encounter. The appointment is the source of truth (precedence 1);
        // encounterNumber is the explicit fallback (precedence 2). Without either,
        // the server falls back to its shared resolver and can 409 when a case has
        // multiple encounters.
        appointmentId: appointment?.id || null,
        // v242.170.0 — the APPOINTMENT still answers first (it names the encounter
        // this check-in is for); the case's own fallback goes through the owner.
        encounterNumber: appointment?.encounterNumber ?? appointment?.encounter_number
          ?? currentEncounterNumber(currentCase),
        demographicsConfirmed: idConfirmed,
        insuranceConfirmed: true,
        copayAcknowledged,
        // v235.2.0 — forward-compat: API consumes this to flip the cassette
        // authorization.confirmedAtCheckIn in v235.3. Null when not auth-required.
        authorizationConfirmed: authRequired ? (authNow === true || authCleared) : null,
        estimatedCopay: (isSelfPay ? selfPayAmount : copayAmount) || null,
        selfPay: isSelfPay,
        // s101 Phase 3: no self-reported vitals; the office takes them.
        vitals: null,
      })

      if (response?.success) {
        setCheckInToken(response.token)
        // v222.10.1: reflect the server's truth rather than hardcoding 'checked_in'.
        // A follow-up check-in keeps status 'in_follow_up' and reports arrival via
        // encounterPhase 'follow_up_checked_in'; only an initial check-in becomes
        // 'checked_in'. Hardcoding flipped follow-up cases to the wrong status locally.
        updateCase({
          status: response.status || 'checked_in',
          ...(response.encounterPhase ? { encounterPhase: response.encounterPhase } : {}),
          checkIn: response.checkIn,
        })
        setStep(IDX.success) // success
        // s126c: no sign-on here; Join asks it (a joinIntent modal joins by itself on this screen)
        if (isVirtualVisit) setSignOn(null)
      } else {
        setError(response?.error || 'Check-in failed')
      }
    } catch (err) {
      console.error('[CheckIn] Error:', err)
      setError(t('checkIn.checkInFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const titles = [t('checkIn.visitDetails'), t('checkIn.verifyIdentity'), t('checkIn.insuranceBilling')]
  // The authorization step had no title of its own and showed the old "Vital signs" heading.
  const stepTitle = step === IDX.authorization ? t('checkInAuth.title', 'Authorization') : titles[step]

  return createPortal(
    <div data-checkin-modal="" className="fixed inset-0 z-[9999] bg-[#F8FAFB] flex flex-col">
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Header */}
      {/* ★ s101 Phase 3 (x_17): the header (and its back arrow) shows on EVERY step before success.
          It was `step < 4`, so with the HMO authorization step the step after it had no way back. */}
      {step !== IDX.success && (
        <div className="px-4 pb-1 flex-shrink-0 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #15354F 0%, #1E4A6E 60%, #245A80 100%)', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
          <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full" style={{ background: 'rgba(70,168,249,0.08)' }} />
          <div className="flex items-center justify-between mb-3 relative z-10">
            <button onClick={() => step > 0 ? setStep(step - 1) : onClose()} className="p-1.5 -ml-1.5">
              <ArrowLeft size={22} className="text-white" />
            </button>
            <button onClick={onClose} className="p-1.5 -mr-1.5">
              <X size={20} className="text-white/50" />
            </button>
          </div>
          <div className="relative z-10">
            <div className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[1.5px] text-[#A7D9FC]/70 bg-white/10 px-3 py-1 rounded-full border border-white/10 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC]" />
              {t('checkIn.checkIn', 'Check-in')}
            </div>
            <h1 className="text-lg font-bold text-white mb-0.5">{stepTitle}</h1>
            <p className="text-[11px] text-white/35">{doctor?.first_name ? clinicianName(doctor) : 'Your doctor'}</p>
          </div>
          <StepDots current={step} total={TOTAL_DOTS} />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4" style={{ paddingTop: step !== IDX.success ? undefined : 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>

        {/* ════ STEP 0: Visit Details ════ */}
        {step === 0 && (
          <div className="space-y-3" style={{ animation: 'fadeUp 0.3s ease' }}>
            <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8]">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9] mb-2">{t('checkIn.appointment')}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-[#46A8F9]" />
                  <span className="text-[14px] font-semibold text-[#15354F]">
                    {(() => {
                      const d = appointment?.date || appointment?.requestedDate || appointment?.confirmedDate
                      if (!d) return 'Date TBD'
                      try {
                        const [y, mo, dy] = d.split('-').map(Number)
                        return new Date(y, mo - 1, dy).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                      } catch { return d }
                    })()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-[#46A8F9]" />
                  <span className="text-[14px] font-medium text-slate-600">
                    {(() => {
                      const t = appointment?.time || appointment?.requestedTime || appointment?.confirmedTime
                      if (!t) return 'Time TBD'
                      const [h, m] = t.split(':').map(Number)
                      const ampm = h >= 12 ? 'PM' : 'AM'
                      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
                      return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`
                    })()} · {appointment?.type === 'video' || appointment?.type === 'telehealth' || appointment?.type === 'virtual' ? 'Virtual visit' : 'In-person'}
                  </span>
                </div>
                {(doctor?.practice_name || doctor?.practice_address) && (
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-[#46A8F9] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[14px] font-medium text-slate-600">{doctor?.practice_name || ''}</span>
                      {/* v222.4.8 — defensive: practice_address may be JSONB dict / JSON-string */}
                      {doctor?.practice_address && <p className="text-[12px] text-[#94A3B8] mt-0.5">{formatAddress(doctor.practice_address)}{doctor.practice_city ? `, ${doctor.practice_city}` : ''}</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {doctor && (
              <div className="bg-gradient-to-br from-[#E9F8FF] to-white rounded-2xl p-4 border-[1.5px] border-[#A7D9FC]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white border-2 border-[#A7D9FC] rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {doctor?.profile_photo || doctor?.photo_url ? (
                      <img src={doctor.profile_photo || doctor.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[#46A8F9] font-bold text-sm">{(doctor.first_name?.[0] || '') + (doctor.last_name?.[0] || '')}</span>
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-[#15354F] text-[14px]">{clinicianName(doctor)}</p>
                    <p className="text-[12px] font-medium text-[#46A8F9]">{((doctor.specialties && doctor.specialties[0]) || '') || 'Specialist'}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="bg-[#F1F5F9] rounded-xl p-3">
              <p className="text-[12px] font-semibold text-[#64748B] mb-1">{t('checkIn.willVerify')}</p>
              {[t('checkIn.willVerifyIdentity'), t('checkIn.willVerifyInsurance'), ...(authRequired ? [t('checkInAuth.title', 'Authorization')] : [])].map((item, i) => (
                <div key={i} className="flex items-center gap-2 mt-1">
                  <div className="w-1 h-1 rounded-full bg-[#46A8F9]" />
                  <span className="text-[12px] text-[#64748B]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ════ STEP 1: Verify Identity ════ */}
        {step === 1 && (
          <div className="space-y-3" style={{ animation: 'fadeUp 0.3s ease' }}>
            <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8]">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9] mb-1">{t('checkIn.personalInformation')}</p>
              <FieldRow label={t('checkIn.fullName')} value={patientName} />
              <FieldRow label={t('checkIn.dateOfBirth')} value={formatDOB(dob)} />
              <FieldRow label={t('checkIn.phone')} value={phone} />
              <FieldRow label={t('checkIn.address')} value={address} last />
            </div>
            <ConfirmCheckbox checked={idConfirmed} onChange={() => setIdConfirmed(!idConfirmed)} label={t('checkIn.confirmInfoCorrect')} />
            <button onClick={() => { onClose(); navigate('/profile') }} className="w-full flex items-center justify-center gap-1.5 py-2 text-[13px] font-semibold text-[#D97706]">
              <Edit3 size={13} /> {t('checkIn.somethingChanged')}
            </button>
          </div>
        )}

        {/* ════ STEP 2: Insurance & Billing ════ */}
        {step === 2 && (
          <div className="space-y-3" style={{ animation: 'fadeUp 0.3s ease' }}>
            <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8]">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9] mb-1">{t('checkIn.insurance')}</p>
              <FieldRow label={t('checkIn.provider')} value={insurance.provider || insurance.insurerName || t('checkIn.notSpecified')} />
              <FieldRow label={t('checkIn.memberId')} value={insurance.memberId || '—'} />
              <FieldRow label={t('checkIn.groupNumber')} value={insurance.groupNumber || '—'} />
              {insurance.planType && <FieldRow label={t('checkIn.planType')} value={(insurance.planType || '').toUpperCase()} last />}
            </div>

            {/* PROD-1132 Q2 (Alex 2026-10-18): no card on file (no carrier + member ID, not self-pay) -> one
                line and a way to Profile > Insurance. Check-in is NOT blocked: nothing here gates Next. */}
            {!isSelfPay && !hasCarrierAndMemberId(insurance) && (
              <div data-testid="checkin-card-missing" className="bg-amber-50 rounded-2xl p-4 border-[1.5px] border-amber-200">
                <p className="text-[13px] text-amber-800">{t('checkIn.addCardForFrontDesk')}</p>
                <button onClick={() => { onClose(); navigate('/profile/insurance') }} className="mt-2.5 w-full py-2 rounded-full text-[13px] font-bold text-white active:scale-[0.98]" style={{ background: '#D97706' }}>
                  {t('myInsurance.addInsurance')}
                </button>
              </div>
            )}

            {/* v237.18.0: Coverage verifier unified into the copay card. For resolved
                non-active results, surface the detailed badge (carries the proper
                inactive/not-found/error copy + CTAs) above the estimated copay. */}
            {eligibilityResolvedNonActive && (
              <EligibilityBadge
                eligibility={eligibility}
                variant="detailed"
                doctorRole={roleFromSpecialty(doctorSpecialty)}
              />
            )}

            {/* v242.79.0 (TF-35) — self-pay gets its own card. Everything below
                is written for someone with a payer: it verifies coverage, names
                the plan, and calls the number a COPAY. None of that is true for
                a self-pay patient, and dressing the self-pay rate up in that
                card would tell them an insurer confirmed a price nobody
                checked. Separate card, no verification claim, and the label
                names the modality because the rate is per modality. */}
            {isSelfPay ? (
              <div className="bg-[#E9F8FF] rounded-2xl p-4 border-[1.5px] border-[#A7D9FC]">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9] mb-2">{t('checkIn.selfPayVisit')}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-[28px] font-bold text-[#15354F]">{selfPayAmount || '—'}</span>
                  {selfPayAmount && (
                    <span className="text-[13px] text-[#64748B]">
                      {isVirtualVisit ? t('checkIn.virtualVisit') : t('checkIn.inPersonVisit')} · {t('checkIn.dueAtVisit')}
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-[#64748B] mt-2">
                  {selfPayAmount
                    ? t('checkIn.selfPayRateNote')
                    : t('checkIn.selfPayNoRateNote')}
                </p>
              </div>
            ) : (
            <>
            {/* Unified Coverage & Copay card — verification happens here, copay shows after */}
            <div className="bg-[#E9F8FF] rounded-2xl p-4 border-[1.5px] border-[#A7D9FC]">
              {eligibilityVerifying ? (
                <div className="flex items-center gap-3">
                  <OrellaSpinner size={18} />
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9]">{t('checkIn.verifyingCoverage')}</p>
                    <p className="text-[13px] text-[#64748B] truncate">{t('checkIn.checkingCoverageWith', { payer: payerName })}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9]">
                      {copayIsVerified ? t('checkIn.copayCard.verifiedCopay') : t('checkIn.copayCard.estimatedCopay')}
                    </p>
                    {copayIsVerified && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                            style={{ background: 'rgba(102,209,112,0.15)', color: '#15354F', border: '1px solid #66D170' }}>
                        <Check size={9} style={{ color: '#66D170' }} strokeWidth={3} /> {t('checkIn.copayCard.verifiedBadge')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-[28px] font-bold text-[#15354F]">{copayAmount || '—'}</span>
                    {copayAmount && <span className="text-[13px] text-[#64748B]">{t('checkIn.copayCard.labelDueAtVisit', { label: copayLabel })}</span>}
                  </div>
                  <p className="text-[12px] text-[#64748B] mt-2">
                    {copayIsVerified
                      ? t('checkIn.copayCard.confirmedBy', { payer: payerName }) + ' ' + t('checkIn.copayCard.finalMayVary')
                      : (copayAmount ? t('checkIn.copayCard.basedOnPlan', { payer: payerName }) : t('checkIn.copayCard.notOnFile')) + ' ' + t('checkIn.copayCard.finalMayVary')}
                  </p>
                  <NetworkDisclaimer className="mt-2" />
                  {/* ★ PROD-689: the API refused a fresh check (recheck_limit / recheck_unavailable);
                      the card shows the last result, says so, and check-in goes on. */}
                  {limitNotice && limitNoticeKey(limitNotice) && hasText(limitNoticeKey(limitNotice)) && (
                    <p data-limit-notice={limitNotice.kind} className="text-[12px] text-[#64748B] mt-2 flex items-start gap-1.5">
                      <Clock size={12} className="shrink-0 mt-[2px]" style={{ color: '#D9912B' }} />
                      <span>{t(limitNoticeKey(limitNotice), limitNoticeParams(limitNotice, language === 'es' ? 'es-US' : 'en-US', (iso) => fmtRelative(iso, t)))}</span>
                    </p>
                  )}
                </>
              )}
            </div>
            </>
            )}
            <ConfirmCheckbox checked={copayAcknowledged} onChange={() => setCopayAcknowledged(!copayAcknowledged)}
                             label={isSelfPay ? 'I understand the cost of this visit' : 'I understand my copay responsibility'} />
            <button onClick={() => { onClose(); navigate('/profile/insurance') }} className="w-full flex items-center justify-center gap-1.5 py-2 text-[13px] font-semibold text-[#D97706]">
              <Edit3 size={13} /> {t('checkIn.updateMyInsurance', 'Update my insurance information')}
            </button>
          </div>
        )}

        {/* ════ STEP: Authorization (auth-required only) ════ */}
        {authRequired && step === IDX.authorization && (
          <div className="space-y-3" style={{ animation: 'fadeUp 0.3s ease' }}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#46A8F9] mb-0.5">{t('checkInAuth.title', 'Authorization')}</p>
            {authOnFile ? (
              <>
                <div data-auth-on-file className="rounded-[14px] p-3.5 border-[1.5px]" style={{ borderColor: '#B9E8C4', background: '#F0FBF3' }}>
                  <div className="flex items-center gap-2 text-[13.5px] font-bold" style={{ color: '#16A34A' }}>
                    <Check size={16} strokeWidth={2.5} /> {t('checkInAuth.onFileTitle', 'Authorization on file')}
                  </div>
                  {mdAuthDoc ? (
                    <p className="text-[12.5px] mt-1" style={{ color: '#15803D' }}>
                      {t('checkInAuth.uploadedBy', 'Uploaded by {{name}}', { name: mdAuthDoc.uploadedByName || t('checkInAuth.yourPhysician', 'your physician') })}
                    </p>
                  ) : null}
                </div>
                <div className="bg-white rounded-2xl p-4 border border-[#E8F1F8]">
                  {caseAuth?.authorizationNumber ? (
                    <>
                      <FieldRow label={t('checkInAuth.authNumber', 'Authorization number')} value={caseAuth.authorizationNumber} />
                      <FieldRow label={t('checkInAuth.authorizedCodes', 'Authorized codes')} value={caseAuth.authorizedCodes} />
                      <FieldRow label={t('checkInAuth.validThrough', 'Valid through')} value={caseAuth.expirationDate || caseAuth.expirationDateISO} last={!mdAuthDoc} />
                    </>
                  ) : null}
                  {mdAuthDoc ? (
                    <button type="button" data-cta="checkin-view-auth" onClick={() => setViewingAuthDoc(true)}
                      className="text-[13px] font-semibold mt-1" style={{ color: BLUE }}>
                      {t('authDocs.viewDocument', 'View document')}
                    </button>
                  ) : null}
                </div>
                <button type="button" data-auth-confirm onClick={() => setAuthConfirmed((v) => !v)}
                  className="w-full flex items-start gap-3 text-left bg-white rounded-2xl p-4 border border-[#E8F1F8]">
                  <span className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: authConfirmed ? '#16A34A' : '#fff', border: `1.7px solid ${authConfirmed ? '#16A34A' : '#CBD5E1'}` }}>
                    {authConfirmed ? <Check size={13} className="text-white" strokeWidth={3} /> : null}
                  </span>
                  <span className="text-[13.5px] text-[#15354F]">{t('checkInAuth.confirmThis', "This is the authorization for today's visit.")}</span>
                </button>
                <StoredDocViewer open={viewingAuthDoc} doc={mdAuthDoc} caseId={currentCase?.id}
                  title={t('checkInAuth.title', 'Authorization')} onClose={() => setViewingAuthDoc(false)} t={t} />
              </>
            ) : (
              <>
                <div className="rounded-[14px] p-3.5 border-[1.5px]" style={{ borderColor: '#FCD9A3', background: '#FEF3C7' }}>
                  <div className="flex items-center gap-2 text-[13.5px] font-bold" style={{ color: '#B45309' }}>
                    <Lock size={15} /> {t('checkInAuth.needed', 'Authorization needed to check in')}
                  </div>
                  <p className="text-[12.5px] mt-1" style={{ color: '#7c4a12' }}>{t('checkInAuth.neededBody', "Your plan requires an approved authorization for today's visit. Add it to continue — or ask the front desk to help.")}</p>
                </div>
                <PatientUploadSection currentCase={currentCase} isHMO={true} hasUploaded={false} onUploaded={() => { setAuthConfirmed(true); handleSubmit(true) }} bookingMode={true} encounterNumber={appointment?.encounterNumber ?? appointment?.encounter_number ?? currentEncounterNumber(currentCase)} />
              </>
            )}
          </div>
        )}

        {/* ════ STEP 4: Success + QR ════ */}
        {step === IDX.success && (
          <div className="flex flex-col items-center text-center pt-6" style={{ animation: 'fadeUp 0.3s ease' }}>
            <div className="w-[72px] h-[72px] rounded-[20px] bg-[#D5EFD3] flex items-center justify-center mb-5">
              <Check size={36} className="text-[#66D170]" strokeWidth={2.5} />
            </div>
            {present ? (
              <div data-checkin-present className="w-full rounded-2xl px-4 py-3.5 mb-5 text-left" style={{ background: '#16A34A' }}>
                <p className="text-[15px] font-bold text-white">{t('checkIn.frontDeskHasYou', 'The front desk has you')}</p>
                <p className="text-[12.5px] text-white/90 mt-0.5">
                  {currentCase?.checkIn?.arrivedAt
                    ? t('checkIn.markedPresentAt', "Marked present at {{time}}. Have a seat; you'll be called in.", { time: new Date(currentCase.checkIn.arrivedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) })
                    : t('checkIn.markedPresent', "You're marked present. Have a seat; you'll be called in.")}
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-[22px] font-bold text-[#15354F] mb-2">{t('checkIn.youreCheckedIn')}</h2>
                <p className="text-[14px] text-[#64748B] leading-relaxed mb-6">
                  {isVirtualVisit
                    // s126c: "You can join now" only once Join is open (15 minutes before the visit)
                    ? t(joinIsOpen(visitJoin || appointment) ? 'videoSignOn.readyBody' : 'videoSignOn.readyBodyLater', { doctor: passProviderName, time: passTimeLabel })
                    : t('checkIn.showCodeOnArrival', "You're checked in on the app. Show this code at the front desk when you arrive.")}
                </p>
                {isVirtualVisit && signOn && (
                  <div data-signon-rows className="w-full rounded-2xl border border-[#DCE6EE] px-4 py-3.5 mb-5 flex flex-col gap-2.5 text-left">
                    {[
                      [t('videoSignOn.rowSignedIn'), patientName],
                      [t('videoSignOn.rowLocation'), stateName(signOn.locationState)],
                      [t('videoSignOn.rowConsent'), t('videoSignOn.agreedAt', { time: signOn.consentAgreedAt ? new Date(signOn.consentAgreedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '' })],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2.5 text-[14px]">
                        <Check size={16} strokeWidth={3} className="text-[#166534]" />
                        <span className="flex-1 font-bold text-[#15354F]">{k}</span>
                        <span className="text-[#5F7A8E]">{v}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* QR Code — s114: never on a video visit (nothing to show at a front desk) */}
            {!isVirtualVisit && (
            <div className="bg-white rounded-2xl p-6 border-[1.5px] border-[#E8F1F8] w-full max-w-[260px] mb-5">
              {checkInToken && (
                <div className="flex justify-center mb-3">
                  <QRCodeDisplay value={checkInToken} size={180} color={NAVY} />
                </div>
              )}
              <p className="text-[11px] text-[#94A3B8] text-center">
                {t('checkIn.checkInCodeLabel', 'Check-in code:')} <span className="font-bold text-[#15354F]">{checkInToken || '...'}</span>
              </p>
            </div>
            )}

            {/* v225.0.x: Link to the designed Wallet-style Visit Pass (client-side
                view; works today, independent of the server pkpass/Google certs). */}
            {checkInToken && !isVirtualVisit && (
              <button
                onClick={() => setShowWalletPass(true)}
                className="w-full max-w-[260px] mb-5 text-[14px] font-semibold text-white rounded-xl px-4 py-3 flex items-center justify-center gap-2"
                style={{ backgroundColor: NAVY }}
              >
                <Wallet size={16} /> {t('checkIn.viewVisitPass', 'View visit pass')}
              </button>
            )}

            {/* MEET PA2: a checked-in video visit joins from here, through JoinVideoVisit (the
                recording question first). Renders nothing while the visit has no link. */}
            {isVirtualVisit && visitJoin && ( /* s126c: Join asks the sign-on first (once per visit) */
              <JoinVideoVisit
                appointment={visitJoin}
                autoJoin={joinIntent}
                beforeOpen={signOnApptId ? (proceed) => {
                  if (signOn) { proceed(); return }
                  pendingJoinRef.current = proceed
                  setSignOnOpen(true)
                } : null}
                className="w-full max-w-[260px] mb-5 text-[14px] font-semibold text-white rounded-xl px-4 py-3 flex items-center justify-center gap-2"
                style={{ backgroundColor: BLUE }}
              />
            )}

            {/* ★ v242.88.0 — buttons, not anchors. An <a href> to the pkpass is a
                TOP-LEVEL NAVIGATION: Wallet takes the pass and the web view is
                left on the pkpass URL with no app behind it. See walletPass.js.
                These also used `import.meta.env.VITE_API_URL || ''`, which
                resolves to a RELATIVE path when the var is unset — a second way
                for the same button to go nowhere. API_BASE is the one binding. */}
            {APPLE_WALLET_ENABLED && currentCase?.id && !isVirtualVisit && (
              <div className="flex flex-col gap-2 w-full max-w-[260px] mb-5">
                <button
                  onClick={handleAddToWallet}
                  disabled={walletBusy}
                  data-wallet-add="apple"
                  className="text-[13px] font-semibold text-white rounded-xl px-4 py-2.5 text-center disabled:opacity-60"
                  style={{ backgroundColor: NAVY }}
                >
                  {walletBusy ? t('checkIn.preparingPass') : t('checkIn.addToAppleWallet')}
                </button>
                {GOOGLE_WALLET_ENABLED && (
                  <button
                    onClick={() => {}}
                    className="text-[13px] font-semibold rounded-xl px-4 py-2.5 text-center border-[1.5px]"
                    style={{ borderColor: NAVY, color: NAVY }}
                  >
                    {t('checkIn.addToGoogleWallet')}
                  </button>
                )}
                {walletError && <p className="text-[12px] text-red-500 text-center">{walletError}</p>}
              </div>
            )}

            {/* Status badge */}
            <div className="inline-flex items-center gap-2 bg-[#E9F8FF] border-[1.5px] border-[#A7D9FC] rounded-xl px-4 py-2.5 mb-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BLUE }} />
              <span className="text-[13px] font-semibold text-[#15354F]">{t('checkIn.statusCheckedIn')}</span>
            </div>
            {!isVirtualVisit && <p className="text-[11px] text-[#94A3B8]">{t('checkIn.showCodeFrontDesk')}</p>}
          </div>
        )}

        {/* Action buttons — inside scroll area so they're always reachable */}
        <div className="mt-4" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))' }}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3">
              <p className="text-[12px] text-red-600">{error}</p>
            </div>
          )}

          {step === 0 && (
            <button onClick={() => setStep(1)}
              className="w-full py-3.5 rounded-full text-white text-[15px] font-bold flex items-center justify-center gap-2 breathe-navy"
              style={{ backgroundColor: NAVY }}>
              {t('checkIn.beginCheckIn', 'Begin check-in')}
            </button>
          )}

          {step === 1 && (
            <button onClick={() => idConfirmed && setStep(2)} disabled={!idConfirmed}
              className={`w-full py-3.5 rounded-full text-white text-[15px] font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-40 ${idConfirmed ? 'breathe-navy' : ''}`}
              style={{ backgroundColor: idConfirmed ? NAVY : '#CBD5E1' }}>
              {t('common.continue', 'Continue')}
            </button>
          )}

          {step === 2 && authRequired && (
            <button onClick={() => copayAcknowledged && setStep(IDX.authorization)} disabled={!copayAcknowledged}
              className={`w-full py-3.5 rounded-full text-white text-[15px] font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-40 ${copayAcknowledged ? 'breathe-navy' : ''}`}
              style={{ backgroundColor: copayAcknowledged ? NAVY : '#CBD5E1' }}>
              {t('common.continue', 'Continue')}
            </button>
          )}

          {/* The last step before success completes check-in: Insurance (no HMO authorization)
              or Authorization (HMO, once confirmed). The vitals step that used to do it is gone. */}
          {((step === 2 && !authRequired) || (step === IDX.authorization && authOnFile)) && (() => {
            const ready = step === 2 ? copayAcknowledged : authCleared
            return (
              <button data-cta="checkin-complete" onClick={() => ready && handleSubmit()} disabled={!ready || submitting}
                className={`w-full py-3.5 rounded-full text-white text-[15px] font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-40 ${ready && !submitting ? 'breathe-green' : ''}`}
                style={{ backgroundColor: submitting ? '#94A3B8' : ready ? GREEN : '#CBD5E1' }}>
                {submitting ? <><OrellaSpinner size={18} tone="inherit" /> {t('checkIn.checkingIn')}</> : <><Check size={18} strokeWidth={2.5} /> {t('checkIn.completeCheckIn')}</>}
              </button>
            )
          })()}

          {step === IDX.success && (
            <button onClick={onClose}
              className="w-full py-3.5 rounded-full border-[1.5px] border-[#D8E6F0] bg-white text-[#15354F] text-[14px] font-semibold flex items-center justify-center gap-2">
              <ArrowLeft size={16} /> {t('checkIn.backToCase', 'Back to Case')}
            </button>
          )}
        </div>
      </div>

      {signOnOpen && signOnApptId && (
        <VideoSignOnFlow
          appointmentId={signOnApptId}
          doctorName={passProviderName}
          patientName={patientName}
          onDone={(rec) => {
            setSignOn(rec)
            setSignOnOpen(false)
            if (rec) updateCase({ checkIn: { ...(currentCase?.checkIn || {}), videoSignOn: rec } })
            // s126c: the Join that asked for the sign-on goes on to the visit
            const go = pendingJoinRef.current
            pendingJoinRef.current = null
            if (rec && go) go()
          }}
          onCancel={() => { setSignOnOpen(false); pendingJoinRef.current = null; if (joinIntent) onClose() }}
        />
      )}

      <WalletPassModal
        isOpen={showWalletPass}
        onClose={() => setShowWalletPass(false)}
        caseId={currentCase?.id}
        patientName={patientName}
        providerName={passProviderName}
        practiceName={doctor?.practice_name || ''}
        addressLine1={passAddressLine1}
        dateLabel={passDateLabel}
        timeLabel={passTimeLabel}
        token={checkInToken}
      />
    </div>,
    document.body
  )
}
